import isObject from 'lodash/isObject';
import { useEffect, useState } from 'react';

import type { Breakpoint, DeviceType } from '@hrworks/design-system/themes';
import { deviceMediaQueries, mq, type screenSizeMediaQueries } from '@hrworks/design-system/utilities';

const BREAKPOINT_ORDER: readonly Breakpoint[] = ['xl', 'lg', 'md', 'sm', 'xs'] as const;

const getEntries = <T>(input: Partial<Record<Breakpoint, T>> | Partial<Record<DeviceType, T>>) => {
  const isDeviceInput = Object.keys(input).some((key) => key in deviceMediaQueries);

  return isDeviceInput
    ? (Object.keys(input) as DeviceType[]).map((device) => ({
        query: window.matchMedia(deviceMediaQueries[device].slice(7)),
        value: (input as Partial<Record<DeviceType, T>>)[device],
      }))
    : BREAKPOINT_ORDER.filter((breakpoint) => breakpoint in input).map((breakpoint) => ({
        query: window.matchMedia(mq[`>=${breakpoint}` as keyof typeof screenSizeMediaQueries].slice(7)),
        value: (input as Partial<Record<Breakpoint, T>>)[breakpoint],
      }));
};

const resolveValue = <T>(entries: { query: MediaQueryList; value: T | undefined }[]) =>
  entries.find(({ query }) => query.matches)?.value;

/**
 * A hook that returns a responsive value based on the current breakpoint or device type.
 *
 * @example
 * const padding = useResponsiveProp({ xs: '14px', lg: '20px' });
 * const typography = useResponsiveProp({ isSmallDevice: 'headingLg', isLargeDevice: 'title' });
 * const value = useResponsiveProp('10px');
 */
export const useResponsiveProp = <T>(
  input: T | Partial<Record<Breakpoint, T>> | Partial<Record<DeviceType, T>>,
): T | undefined => {
  const isObjectInput = isObject(input);

  const [value, setValue] = useState<T | undefined>(() => {
    if (!isObjectInput) return input;
    if (typeof window === 'undefined') return;

    return resolveValue(getEntries(input));
  });

  useEffect(() => {
    if (!isObjectInput) {
      setValue(input);

      return;
    }

    const entries = getEntries(input);
    let frameId: number;
    const update = () => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => setValue(resolveValue(entries)));
    };

    entries.forEach(({ query }) => query.addEventListener('change', update));

    return () => {
      cancelAnimationFrame(frameId);
      entries.forEach(({ query }) => query.removeEventListener('change', update));
    };
  }, [input, isObjectInput]);

  return value;
};
